/**
 * StudyReferenceDrawer — the recruiter's at-a-glance study reference, as a
 * right slide-over so it can sit NEXT to whatever they're doing (the contact
 * conversation, a live prescreen) instead of navigating away mid-call.
 *
 * Three tabs from the published study assets only (drafts never leak here):
 *   Summary — the published 'summary' artifact ({text}, MarkdownText)
 *   Script  — the published 'recruiter_script' artifact ({text}, MarkdownText)
 *   Stipend — studies.stipend_summary (the live value; stipend_info artifacts
 *             publish INTO it and admins hand-edit the same field)
 *
 * Study details are session-cached per study (module Map) — recruiters flip
 * the drawer open/closed constantly during a call and published assets change
 * rarely; the refresh button busts the cache when they don't trust it.
 *
 * Layering: z-40, deliberately BELOW the SoftphoneBar (z-50) — the drawer is
 * meant to stay open while talking, and mute/hang-up must stay clickable.
 * The view is keyed to the selection: switching studies clears to the cached
 * value or a spinner, and async commits are dropped when the selection has
 * moved on — a recruiter must never read study A's stipend under study B's
 * name.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';
import api from '../../../services/api';
import { StudyDetailResponse, StudyOption, studyInternalLabel } from '../../types';
import MarkdownText from '../MarkdownText';
import AiWizardPanel from '../callmode/AiWizardPanel';
import { openStudyDoc } from '../forms/openStudyDoc';
import { publishedArtifact } from './TextArtifactPanel';

type RefTab = 'summary' | 'script' | 'stipend' | 'ai';

// Session caches (cleared on full page load). Studies list is shared; details
// cache per study id. Both are tiny — text artifacts, no documents content.
let studiesCache: StudyOption[] | null = null;
const detailCache = new Map<string, StudyDetailResponse>();

// Stable default — an inline [] default prop would give the options memo a new
// identity every render and re-fire the selection effect in a loop.
const NO_SLUGS: string[] = [];

const iconBtn =
  'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0';

interface StudyReferenceDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Default selection on open (e.g. the running prescreen's study). */
  initialStudyId?: string | null;
  /** Slugs of the contact's lead-linked studies (ordering + default pick). */
  contactStudySlugs?: string[];
}

export default function StudyReferenceDrawer({
  open, onClose, initialStudyId = null, contactStudySlugs = NO_SLUGS,
}: StudyReferenceDrawerProps) {
  const [tab, setTab] = useState<RefTab>('summary');
  const [studies, setStudies] = useState<StudyOption[]>(studiesCache || []);
  const [studiesLoaded, setStudiesLoaded] = useState(studiesCache !== null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<StudyDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The user's manual pick beats the caller's initialStudyId until reopen.
  const pickedByUser = useRef(false);
  // Only the LATEST requested study may commit fetch results — an earlier
  // slow response must never render under a newer selection.
  const wantId = useRef('');
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // CRM studies only (lead-only stubs have no artifacts), contact's studies first.
  const options = useMemo(() => {
    const linked = new Set(contactStudySlugs);
    return studies
      .filter((s) => s.id && s.source === 'crm')
      .sort((a, b) => {
        const aLinked = linked.has(a.slug) ? 0 : 1;
        const bLinked = linked.has(b.slug) ? 0 : 1;
        if (aLinked !== bLinked) return aLinked - bLinked;
        return (b.count || 0) - (a.count || 0);
      });
  }, [studies, contactStudySlugs]);

  const loadStudies = useCallback(async (force = false) => {
    if (studiesCache && !force) {
      setStudies(studiesCache);
      setStudiesLoaded(true);
      return;
    }
    try {
      const res = await api.get('/api/crm/studies');
      studiesCache = res.data?.studies || [];
      setStudies(studiesCache!);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load studies');
    } finally {
      setStudiesLoaded(true);
    }
  }, []);

  const loadDetail = useCallback(async (studyId: string, force = false) => {
    if (!studyId) return;
    wantId.current = studyId;
    const cached = detailCache.get(studyId);
    setDetail(cached && !force ? cached : null);
    setError(null);
    if (cached && !force) {
      // The latest selection is satisfied from cache — release the spinner
      // even if an older fetch is still in flight (its finally is wantId-
      // guarded and would otherwise leave `loading` stuck true forever).
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${studyId}`);
      detailCache.set(studyId, res.data);
      if (wantId.current !== studyId) return; // selection moved on
      setDetail(res.data);
    } catch (e: any) {
      if (wantId.current !== studyId) return;
      setError(e?.response?.data?.error || 'Could not load the study');
    } finally {
      if (wantId.current === studyId) setLoading(false);
    }
  }, []);

  // Each open starts fresh: the caller's study wins again and focus moves
  // into the panel (restored to the opener on close).
  useEffect(() => {
    if (!open) return;
    pickedByUser.current = false;
    loadStudies();
    openerRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      openerRef.current?.focus?.();
    };
  }, [open, loadStudies]);

  // Default selection: the caller's study, else keep a still-valid pick, else
  // the contact's first lead-linked study (options are ordered that way).
  useEffect(() => {
    if (!open || options.length === 0) return;
    setSelectedId((prev) => {
      if (pickedByUser.current && prev && options.some((s) => s.id === prev)) return prev;
      if (initialStudyId && options.some((s) => s.id === initialStudyId)) return initialStudyId;
      if (prev && options.some((s) => s.id === prev)) return prev;
      return options[0].id!;
    });
  }, [open, options, initialStudyId]);

  useEffect(() => {
    if (open && selectedId) loadDetail(selectedId);
  }, [open, selectedId, loadDetail]);

  // Escape closes; Tab is trapped inside the panel — aria-modal promises the
  // rest of the page is inert, and a focus walk into the obscured prescreen
  // runner could silently mutate answers.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      // Disabled controls (e.g. Refresh while loading) are unfocusable —
      // wrapping onto one would swallow the Tab and strand the keyboard.
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button, select, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      // The SoftphoneBar (z-50) deliberately floats ABOVE this drawer so
      // mute/hang-up stay usable mid-call — never trap focus away from it.
      if (active instanceof HTMLElement && active.closest('[data-softphone]')) return;
      if (!panelRef.current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const refresh = () => {
    if (selectedId) loadDetail(selectedId, true);
    loadStudies(true);
  };

  const summary = detail ? publishedArtifact(detail.artifacts, 'summary') : null;
  const script = detail ? publishedArtifact(detail.artifacts, 'recruiter_script') : null;
  const stipend = detail?.study?.stipend_summary || {};
  const stipendEmpty = !stipend.amount_text && !stipend.schedule_text && !stipend.notes;

  const tabBtn = (key: RefTab, label: string, Icon?: typeof Sparkles) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 min-h-[44px] sm:min-h-0 ${
        tab === key
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      {label}
    </button>
  );

  const emptyHint = (what: string) => (
    <p className="text-sm text-gray-500 dark:text-gray-400">
      No published {what} yet — an admin can generate and publish one under Studies.
    </p>
  );

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Study reference">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl focus:outline-none dark:bg-gray-800"
      >
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex items-center justify-between gap-2">
            <h2 className="inline-flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <BookOpen className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
              <span className="truncate">Study reference</span>
            </h2>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={refresh}
                disabled={loading}
                aria-label="Refresh study info"
                title="Refresh study info"
                className={`${iconBtn} disabled:opacity-50`}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close study reference"
                className={iconBtn}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {options.length > 1 && (
            <select
              value={selectedId}
              onChange={(e) => { pickedByUser.current = true; setSelectedId(e.target.value); }}
              aria-label="Study"
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {options.map((s) => (
                <option key={s.id} value={s.id!}>{studyInternalLabel(s)}</option>
              ))}
            </select>
          )}
          {options.length === 1 && (
            <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{studyInternalLabel(options[0])}</p>
          )}

          <div className="mt-2 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-900/60">
            {tabBtn('summary', 'Summary')}
            {tabBtn('script', 'Script')}
            {tabBtn('stipend', 'Stipend')}
            {tabBtn('ai', 'Ask AI', Sparkles)}
          </div>
        </div>

        {/* AI tab hosts AiWizardPanel (its own bounded scroll + bottom composer),
            so it gets a non-scrolling full-height flex host. The pb-24 here keeps
            the composer clear of the floating SoftphoneBar (z-50) during a call. */}
        {tab === 'ai' ? (
          <div className="min-h-0 flex-1 overflow-hidden p-4 pb-24">
            {selectedId ? (
              <div className="flex h-full min-h-0 flex-col">
                <AiWizardPanel studyId={selectedId} onOpenDoc={openStudyDoc} />
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {studiesLoaded && options.length === 0
                  ? 'No CRM studies yet — set one up under Studies first.'
                  : 'Select a study to ask the AI.'}
              </p>
            )}
          </div>
        ) : (
        /* pb-24 keeps the scroll end readable above the floating SoftphoneBar. */
        <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-24">
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {!studiesLoaded ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : options.length === 0 && !error ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No CRM studies yet — set one up under Studies first.
            </p>
          ) : loading && !detail ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : detail ? (
            <>
              {tab === 'summary' && (summary?.content?.text
                ? <MarkdownText text={summary.content.text} />
                : emptyHint('summary'))}

              {tab === 'script' && (script?.content?.text
                ? <MarkdownText text={script.content.text} />
                : emptyHint('recruiter script'))}

              {tab === 'stipend' && (stipendEmpty ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No compensation details yet — an admin can add them on the study's Overview tab.
                </p>
              ) : (
                <div>
                  {stipend.amount_text && (
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stipend.amount_text}</p>
                  )}
                  {stipend.schedule_text && (
                    <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{stipend.schedule_text}</p>
                  )}
                  {stipend.notes && (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{stipend.notes}</p>
                  )}
                </div>
              ))}
            </>
          ) : null}
        </div>
        )}
      </div>
    </div>
  );
}
